import * as THREE from 'three/webgpu';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {refitRaycastAccelerationForGeometry} from '../../raycast-acceleration';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';

/** 完整闭合曲线首尾采样点去重使用的距离平方容差。 */
const CONTOUR_DUPLICATE_ENDPOINT_DISTANCE_SQUARED =
    SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition ** 2;

/**
 * 草图轮廓 Geometry 更新器。
 *
 * 职责：
 * 1. 把世界坐标轮廓点投影到草图参考面并完成三角剖分；
 * 2. 顶点与索引布局不变时原位更新现有缓冲，避免拖拽热路径反复创建 GPU 资源；
 * 3. 布局变化时创建新的 ShapeGeometry，由轮廓对象负责替换和释放旧资源。
 */
export class SketchContourGeometryUpdater {
    /** 可复用的局部二维轮廓点缓冲。 */
    private readonly localPoints: THREE.Vector2[] = [];

    /** 可复用的三角形索引扁平缓冲。 */
    private readonly triangleIndices: number[] = [];

    /** 局部坐标转世界坐标时复用的临时向量。 */
    private readonly worldPointBuffer = new THREE.Vector3();

    /** 当前轮廓所属参考面。 */
    private readonly referencePlane: ReferencePlane;

    /**
     * 创建草图轮廓 Geometry 更新器。
     *
     * @param referencePlane 轮廓所属参考面。
     */
    public constructor(referencePlane: ReferencePlane) {
        this.referencePlane = referencePlane;
    }

    /**
     * 创建一份新的轮廓 ShapeGeometry。
     *
     * @param points 世界坐标轮廓点。
     * @returns 有效 ShapeGeometry；点数不足时返回 null。
     */
    public createGeometry(points: readonly THREE.Vector3[]): THREE.ShapeGeometry | null {
        if (!this.prepareLocalPoints(points)) {
            return null;
        }
        return this.createPreparedGeometry();
    }

    /**
     * 更新既有轮廓 Geometry。
     *
     * 只有 position、uv 与 index 的元素数量全部一致时才原位写入；任一布局变化均回退
     * 到新 Geometry，避免 TypedArray 越界或残留旧三角形。
     *
     * @param geometry 当前轮廓 Geometry。
     * @param points 最新世界坐标轮廓点。
     * @returns 可继续使用的 Geometry；输入无效时返回 null。
     */
    public updateGeometry(
        geometry: THREE.BufferGeometry,
        points: readonly THREE.Vector3[],
    ): THREE.BufferGeometry | null {
        if (!this.prepareLocalPoints(points)) {
            return null;
        }

        this.triangulatePreparedPoints();
        if (!this.canUpdateInPlace(geometry)) {
            return this.createPreparedGeometry();
        }

        this.writePreparedPointsToGeometry(geometry);
        return geometry;
    }

    /**
     * 将世界点写入复用的局部点缓冲，并统一为 ShapeGeometry 使用的顺时针方向。
     *
     * @param points 世界坐标轮廓点。
     * @returns 是否至少保留三个有效点。
     */
    private prepareLocalPoints(points: readonly THREE.Vector3[]): boolean {
        if (!Array.isArray(points) || points.length < 3) {
            return false;
        }

        // 仅在顶点数量增长时创建 Vector2；连续拖拽复用已有对象。
        while (this.localPoints.length < points.length) {
            this.localPoints.push(new THREE.Vector2());
        }
        this.localPoints.length = points.length;
        points.forEach((point, index) => {
            const localPoint = this.localPoints[index];
            if (localPoint) {
                this.referencePlane.worldToLocalTo(point, localPoint);
            }
        });

        /**
         * 按统一闭合曲线容差移除重复末点，禁止依赖浮点精确相等。
         * 完整椭圆平移前的末点可能保留约 1e-16 的三角函数误差，平移后经 Float32 量化又会
         * 与首点完全相等；若只用 equals，顶点布局会在 65/64 之间跳变并破坏 Geometry 复用和索引顺序。
         */
        const firstPoint = this.localPoints[0];
        const lastPoint = this.localPoints[this.localPoints.length - 1];
        if (
            firstPoint
            && lastPoint
            && this.localPoints.length > 3
            && firstPoint.distanceToSquared(lastPoint) <= CONTOUR_DUPLICATE_ENDPOINT_DISTANCE_SQUARED
        ) {
            this.localPoints.pop();
        }
        if (this.localPoints.length < 3) {
            return false;
        }

        // ShapeGeometry 会把外轮廓统一为顺时针；提前归一化可保证原位缓冲顺序与首次创建一致。
        if (!THREE.ShapeUtils.isClockWise(this.localPoints)) {
            this.localPoints.reverse();
        }
        return true;
    }

    /**
     * 对当前局部点执行三角剖分并写入复用索引数组。
     */
    private triangulatePreparedPoints(): void {
        const triangles = THREE.ShapeUtils.triangulateShape(this.localPoints, []);
        this.triangleIndices.length = 0;
        triangles.forEach((triangle) => {
            triangle.forEach((vertexIndex) => {
                this.triangleIndices.push(vertexIndex);
            });
        });
    }

    /**
     * 判断既有 Geometry 的所有缓冲布局是否允许原位更新。
     *
     * @param geometry 当前轮廓 Geometry。
     * @returns 所有缓冲元素数量一致时返回 true。
     */
    private canUpdateInPlace(geometry: THREE.BufferGeometry): boolean {
        const position = geometry.getAttribute('position');
        const uv = geometry.getAttribute('uv');
        const index = geometry.getIndex();

        return position instanceof THREE.BufferAttribute
            && uv instanceof THREE.BufferAttribute
            && index instanceof THREE.BufferAttribute
            && position.count === this.localPoints.length
            && uv.count === this.localPoints.length
            && index.count === this.triangleIndices.length;
    }

    /**
     * 将已准备的顶点、纹理坐标与索引原位写入既有 Geometry。
     *
     * @param geometry 当前轮廓 Geometry。
     */
    private writePreparedPointsToGeometry(geometry: THREE.BufferGeometry): void {
        const position = geometry.getAttribute('position') as THREE.BufferAttribute;
        const uv = geometry.getAttribute('uv') as THREE.BufferAttribute;
        const index = geometry.getIndex() as THREE.BufferAttribute;

        this.localPoints.forEach((localPoint, pointIndex) => {
            this.referencePlane.localToWorldTo(localPoint, this.worldPointBuffer);
            position.setXYZ(
                pointIndex,
                this.worldPointBuffer.x,
                this.worldPointBuffer.y,
                this.worldPointBuffer.z,
            );
            uv.setXY(pointIndex, localPoint.x, localPoint.y);
        });
        this.triangleIndices.forEach((vertexIndex, indexOffset) => {
            index.setX(indexOffset, vertexIndex);
        });

        position.needsUpdate = true;
        uv.needsUpdate = true;
        index.needsUpdate = true;
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        // 场景若已为该轮廓建立 BVH，原位改写后同步校正其包围结构。
        refitRaycastAccelerationForGeometry(geometry);
    }

    /**
     * 使用已准备的局部点创建新的 ShapeGeometry 并变换到世界坐标。
     *
     * @returns 新创建的 ShapeGeometry。
     */
    private createPreparedGeometry(): THREE.ShapeGeometry {
        const shape = new THREE.Shape(this.localPoints.map((point) => point.clone()));
        const geometry = new THREE.ShapeGeometry(shape);
        const transform = new THREE.Matrix4().makeBasis(
            this.referencePlane.xAxis,
            this.referencePlane.yAxis,
            this.referencePlane.normal,
        );
        transform.setPosition(this.referencePlane.originWorld);
        geometry.applyMatrix4(transform);
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();
        return geometry;
    }
}
